/**
 * Nummora free gifts (no app). Each gift is a separate $0 product, hidden from search:
 *   1+ kits -> Free Whiteout Pen (free-whiteout-pen-gift)
 *   3+ kits -> Free 10-Piece Brush Set (free-10-piece-brush-set-gift)
 * (2 kits = 2nd kit 50% OFF is a normal automatic discount.)
 * A gift is added automatically (line property _nm_gift) once the cart has enough kits, removed again
 * when it no longer qualifies, and trimmed to quantity 1. Separate products are used because a
 * Buy X Get Y discount cannot share the same kits with "Buy 1, Get 1 50% OFF".
 * If the shopper removes a gift, we respect that for the session; the cart progress box then shows an
 * "Add" button (data-nm-gift-add="<variant id>").
 * Cart writes share one queue with nm-upsell.js (window.nmCartQueue) so a gift and an add-on sent at
 * the same moment can't overwrite each other.
 * Config: window.nmGifts = { kitType, gifts: [{ variant, min }] } (snippets/nm-scroll-top.liquid).
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const cfg = window.nmGifts || { gifts: [] };
const gifts = (cfg.gifts || []).filter((g) => g.variant).map((g) => ({ variant: Number(g.variant), min: Number(g.min) || 1 }));
const giftIds = new Set(gifts.map((g) => g.variant));
let busy = false;
let again = false;

/** Runs cart writes one at a time (shared with nm-upsell.js). */
if (!window.nmCartQueue) {
  let tail = Promise.resolve();
  window.nmCartQueue = (task) => {
    const run = tail.then(task, task);
    tail = run.catch(() => {});
    return run;
  };
}
const queue = (task) => window.nmCartQueue(task);

const routes = () => (window.Theme && window.Theme.routes) || {};
const jsUrl = (url, fallback) => (url || fallback).replace(/\.js$/, '') + '.js';
const declinedKey = (v) => 'nm_gift_declined_' + v;
const store = {
  get: (v) => { try { return sessionStorage.getItem(declinedKey(v)) === '1'; } catch (e) { return false; } },
  set: (v, on) => { try { on ? sessionStorage.setItem(declinedKey(v), '1') : sessionStorage.removeItem(declinedKey(v)); } catch (e) {} markDeclined(); },
};

/**
 * The cart progress box only shows its "+ Add your free …" button for a gift the shopper removed themselves.
 * The ids of those gifts are kept on <html data-nm-gift-declined="…"> (CSS in snippets/nm-cart-promo.liquid),
 * so the button no longer flashes in the moment between adding a kit and the gift being added automatically.
 */
function markDeclined() {
  const ids = gifts.filter((g) => store.get(g.variant)).map((g) => g.variant);
  document.documentElement.dataset.nmGiftDeclined = ids.join(' ');
}
markDeclined();

function sectionIds() {
  const ids = new Set(['cart-drawer-section']);
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => ids.add(el.dataset.sectionId));
  return [...ids];
}

const isGift = (i) => giftIds.has(i.variant_id) || Boolean(i.properties && i.properties._nm_gift);
const kitCount = (items) =>
  items.filter((i) => !isGift(i) && i.product_type === cfg.kitType).reduce((n, i) => n + i.quantity, 0);

async function getCart() {
  return (await fetch(jsUrl(routes().cart_url, '/cart'), { headers: { Accept: 'application/json' } })).json();
}

/** Runs a cart mutation and tells Horizon (cart drawer, cart icon) about it. */
async function mutate(action, lines, request) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
  try {
    const { res, data, cart } = await queue(async () => {
      const res = await request();
      const data = await res.json();
      const cart = await getCart();
      return { res, data, cart };
    });
    const failed = !res.ok || Boolean(data.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'nm-gift',
        sections: failed ? undefined : data.sections,
        itemCount: cart.item_count,
        didError: failed,
      },
    });
    return !failed;
  } catch (error) {
    deferred.reject(error);
    return false;
  }
}

function addGift(variant) {
  return mutate('add', [{ merchandiseId: String(variant), quantity: 1 }], () =>
    fetch(jsUrl(routes().cart_add_url, '/cart/add'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variant, quantity: 1, properties: { _nm_gift: 'Free gift' } }],
        sections: sectionIds().join(','),
      }),
    })
  );
}

function setLine(key, quantity) {
  return mutate(quantity ? 'update' : 'remove', [{ id: key, quantity }], () =>
    fetch(jsUrl(routes().cart_change_url, '/cart/change'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity, sections: sectionIds().join(',') }),
    })
  );
}

async function sync(items) {
  if (!gifts.length) return;
  // A cart change arrived while we were busy: check again once we're done, with a fresh cart.
  if (busy) { again = true; return; }
  busy = true;
  try {
    // Read the cart inside the queue so pending add-ons are already in it.
    let list = (await queue(() => getCart())).items || items || [];
    const kits = kitCount(list);
    for (const g of gifts) {
      const lines = list.filter((i) => i.variant_id === g.variant);
      let changed = false;
      if (kits >= g.min) {
        if (!lines.length && !store.get(g.variant)) changed = await addGift(g.variant);
        else if (lines.length > 1 || (lines[0] && lines[0].quantity > 1)) {
          await setLine(lines[0].key, 1);
          for (const l of lines.slice(1)) await setLine(l.key, 0);
          changed = true;
        }
      } else if (lines.length) {
        store.set(g.variant, false);
        for (const l of lines) await setLine(l.key, 0);
        changed = true;
      }
      if (changed) list = (await getCart()).items || [];
    }
    // Stray gift-flagged lines that are no longer a configured gift.
    for (const l of list.filter((i) => i.properties && i.properties._nm_gift && !giftIds.has(i.variant_id))) {
      await setLine(l.key, 0);
    }
  } finally {
    busy = false;
    if (again) { again = false; sync(); }
  }
}

if (!window.__nmGiftBound && gifts.length) {
  window.__nmGiftBound = true;
  let lastGiftLines = [];

  document.addEventListener(CartLinesUpdateEvent.eventName, (event) => {
    const detailPromise = event.promise;
    if (!detailPromise) return;
    // Shopper removed a gift line themselves: remember it for this session.
    if (!busy && (event.detail?.action === 'remove' || event.action === 'remove')) {
      const ids = (event.detail?.lines || event.lines || []).map((l) => l.id);
      lastGiftLines.filter((l) => ids.includes(l.key)).forEach((l) => store.set(l.variant_id, true));
    }
    detailPromise
      .then((result) => {
        const items = result?.detail?.items;
        if (items) lastGiftLines = items.filter(isGift);
        if (result?.detail?.source === 'nm-gift') return;
        return sync(items);
      })
      .catch(() => {});
  });

  // "Add" buttons in the cart progress box (shown after the shopper removed a gift).
  document.addEventListener('click', (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-nm-gift-add]') : null;
    if (!btn) return;
    event.preventDefault();
    btn.disabled = true;
    store.set(Number(btn.dataset.nmGiftAdd), false);
    sync();
  });

  getCart()
    .then((cart) => {
      lastGiftLines = (cart.items || []).filter(isGift);
      return sync(cart.items);
    })
    .catch(() => {});
}
